// ============================================================================
// ORDERS · Collection — the ONE set of predicates the Wall, the Table and the
// drawer share (C4, Convention #61)
// ============================================================================
// Every function here takes the card object collection_cards() answers (53
// keys, C3; 54 with pp_status, C4·0) and nothing else. Components own no
// digit and no rule: the pill, the flag dot, the search, the facets, the sort
// and the grouping are all decided here, so a rule changed once changes on
// every surface. Pure, synchronous, tested in collectionModel.test.js.
// ============================================================================

/** The six PROBLEM flags — the ones that light the card's dot and count as an
 *  exception. Informational flags (no photo, no base price, awaiting colours,
 *  revived) are deliberately not here. Same list as _collection_cards_build. */
export const PROBLEM_FLAGS = [
  ['f_ordered_despite_out', 'Ordered despite OUT'],
  ['f_ordered_without_decision', 'Ordered without a decision'],
  ['f_dev_code_not_found', 'Development code not found'],
  ['f_link_missing', 'CL link missing'],
  ['f_shape_mismatch', 'Shape ≠ development'],
  ['f_pp_unparsed', 'PP cell not readable'],
];
const DECISION_FLAGS = new Set(['f_ordered_despite_out', 'f_ordered_without_decision', 'f_dev_code_not_found']);

/** The problem flags a card carries, as labels, in PROBLEM_FLAGS order. */
export function problemFlags(card) {
  return PROBLEM_FLAGS.filter(([k]) => card && card[k] === true).map(([, label]) => label);
}
/** Red when a decision-class flag is on, amber for the data-class ones, null when clean. */
export function flagTone(card) {
  if (!card) return null;
  if (PROBLEM_FLAGS.some(([k]) => DECISION_FLAGS.has(k) && card[k] === true)) return 'red';
  if (PROBLEM_FLAGS.some(([k]) => card[k] === true)) return 'amber';
  return null;
}

/** The status pill — precedence top-down (the brief's table, §3.2).
 *  key is the facet value (COLLECTION_STATUSES) · label the words · tone the css. */
export const STATUS_PILLS = {
  man: { label: 'Ordered · manual', tone: 'man', order: 1 },
  out: { label: 'Despite OUT', tone: 'exc', order: 4 },
  nodec: { label: 'No decision', tone: 'excOutline', order: 3 },
  ord: { label: 'Ordered', tone: 'ord', order: 0 },
  pre: { label: 'Pre-order', tone: 'pre', order: 2 },
  await: { label: 'Awaiting', tone: 'await', order: 5 },
  notfound: { label: 'Dev not found', tone: 'excOutline', order: 6 },
};
export function statusOf(card) {
  if (!card) return null;
  let key;
  if (card.promotion != null && card.status !== 'order') key = 'man';
  else if (card.f_ordered_despite_out) key = 'out';
  else if (card.f_ordered_without_decision) key = 'nodec';
  else if (card.status === 'order') key = 'ord';
  else if (card.status === 'preorder') key = 'pre';
  else if (card.status === 'awaiting') key = 'await';
  else if (card.f_dev_code_not_found) key = 'notfound';
  else return null;
  return { key, ...STATUS_PILLS[key] };
}
/** The card's STATUS — order · preorder · awaiting → ord · pre · await, read off `card.status`
 *  alone (C7 STOP A: ONE status rule for the seg, the Status groups and the section tiles; the pill
 *  keeps its problem words and the dot, but never moves a card out of its real status). */
const STATUS_KEY = { order: 'ord', preorder: 'pre', awaiting: 'await' };
export const STATUS_LABEL = { ord: 'Ordered', pre: 'Pre-order', await: 'Awaiting' };
export const statusKeyOf = (card) => STATUS_KEY[card?.status] || null;
/** The status FACET a card falls under: its status key, + man (a manual promotion) and exc (any
 *  problem flag) as OVERLAPPING filters — an ordered card with a flag is both Ordered and Flagged. */
export function statusFacets(card) {
  const out = new Set();
  const k = statusKeyOf(card);
  if (k) out.add(k);
  if (card && card.promotion != null && card.status !== 'order') out.add('man');
  if (flagTone(card)) out.add('exc');
  return out;
}

/* ------------------------------------------------------------- material */
/** The material for DISPLAY (C7 STOP A): the development side writes ACETATE / METAL / INJECTION
 *  where the Book writes Acetate — title-case a word in capitals, leave a 1–2 letter code (TR, PC)
 *  and any mixed-case word as it is. The data is never touched; the facet reads this form too, so
 *  ACETATE and Acetate are one filter value. */
export function displayMaterial(v) {
  if (v == null || v === '') return v;
  // a word of four letters or more in capitals is a shouted word (METAL → Metal); up to three it is an acronym and stays (TR · PC · TPE)
  return String(v).replace(/[A-Za-z]+/g, (w) => (w.length > 3 && w === w.toUpperCase() ? w[0] + w.slice(1).toLowerCase() : w));
}

/* ------------------------------------------------------------- search */
const SEARCH_KEYS = ['code', 'dev_number', 'name', 'supplier', 'pi', 'designer'];
const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
/** Six fields, case-insensitive substring, every whitespace-separated term must hit. */
export function matchesSearch(card, q) {
  const terms = norm(q).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = SEARCH_KEYS.map((k) => norm(card?.[k])).join(' | ');
  return terms.every((t) => hay.includes(t));
}

/* ------------------------------------------------------------- facets */
/** The value a facet reads off the card — arrays for the multi-valued ones. */
export function facetValues(card, facet) {
  switch (facet) {
    case 'supplier': return [card?.supplier || '—'];
    case 'designer': return [card?.designer || '—'];
    case 'material': return [displayMaterial(card?.material) || '—'];
    case 'shape': return [card?.shape || '—'];
    case 'flags': return PROBLEM_FLAGS.filter(([k]) => card?.[k] === true).map(([k]) => k);
    default: return [];
  }
}
/** Empty set = inactive, everything passes; else at least one value must be selected. */
export function matchesValueSet(values, selected) {
  if (!selected || selected.length === 0) return true;
  const set = new Set(selected.map(String));
  return (values || []).some((v) => set.has(String(v)));
}
/** Distinct facet values with counts, sorted by count then name — the popover's lists. */
export function facetOptions(cards, facet) {
  const counts = new Map();
  (cards || []).forEach((c) => facetValues(c, facet).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1)));
  const label = (v) => (facet === 'flags' ? (PROBLEM_FLAGS.find(([k]) => k === v) || [v, v])[1] : v);
  // the blank ('—') always last, whatever its count
  return [...counts.entries()].map(([value, n]) => ({ value, label: label(value), n }))
    .sort((a, b) => (a.value === '—') - (b.value === '—') || b.n - a.n || String(a.label).localeCompare(String(b.label)));
}

/** The whole filter in one pass: search · status facet (multi, OR within) · the popover facets (AND across, OR within). */
export function applyFilters(cards, { q = '', status = [], facets = {} } = {}) {
  return (cards || []).filter((c) => {
    if (!matchesSearch(c, q)) return false;
    if (status.length) { const f = statusFacets(c); if (!status.some((s) => f.has(s))) return false; }
    return Object.entries(facets || {}).every(([facet, sel]) => matchesValueSet(facetValues(c, facet), sel));
  });
}

/* --------------------------------------------------------------- sort */
const num = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const desc = (get) => (a, b) => { const x = num(get(a)), y = num(get(b)); if (x === y) return 0; if (x == null) return 1; if (y == null) return -1; return y - x; };
const text = (get) => (a, b) => String(get(a) || '').localeCompare(String(get(b) || ''), 'en', { numeric: true });
export const SORTS = {
  code: { label: 'Code', cmp: text((c) => c.code) },
  pieces: { label: 'Pieces ↓', cmp: desc((c) => c.qty) },
  amount: { label: 'Amount ↓', cmp: desc((c) => c.amount) },
  base: { label: 'Base $', cmp: desc((c) => c.base_price) },
  supplier: { label: 'Supplier', cmp: text((c) => c.supplier) },
  newest: { label: 'Newest order', cmp: (a, b) => String(b.order_date || '').localeCompare(String(a.order_date || '')) },
};
/** Stable: ties fall back to the code so a re-render never reorders. */
export function sortCards(cards, sort) {
  const s = SORTS[sort] || SORTS.pieces;
  return [...(cards || [])].sort((a, b) => s.cmp(a, b) || SORTS.code.cmp(a, b));
}

/* -------------------------------------------------------------- groups */
export const GROUPS = { none: 'None', status: 'Status', supplier: 'Supplier', designer: 'Designer', shape: 'Shape', stage: 'Left stage' };
const STATUS_GROUP_ORDER = ['Ordered', 'Pre-order', 'Awaiting'];
export function groupKeyOf(card, group) {
  switch (group) {
    case 'status': { const k = statusKeyOf(card); return k ? STATUS_LABEL[k] : 'No status'; }
    case 'supplier': return card.supplier || '—';
    case 'designer': return card.designer || '—';
    case 'shape': return card.shape || '—';
    case 'stage': return card.left_stage || (card.locked_since ? 'No stage' : 'Not locked');
    default: return 'All models';
  }
}
/** [{ key, cards, pieces, amount }] — status groups in pill order, the rest by size. */
export function groupCards(cards, group) {
  const map = new Map();
  (cards || []).forEach((c) => { const k = groupKeyOf(c, group); if (!map.has(k)) map.set(k, []); map.get(k).push(c); });
  const rows = [...map.entries()].map(([key, list]) => ({
    key, cards: list,
    pieces: list.reduce((s, c) => s + (num(c.qty) || 0), 0),
    amount: list.reduce((s, c) => s + (num(c.amount) || 0), 0),
  }));
  if (group === 'status') {
    const order = STATUS_GROUP_ORDER;
    rows.sort((a, b) => { const ia = order.indexOf(a.key), ib = order.indexOf(b.key); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib); });
  } else if (group !== 'none') rows.sort((a, b) => b.cards.length - a.cards.length || a.key.localeCompare(b.key));
  return rows;
}

/* ------------------------------------------------------------- format */
export const fmtInt = (v) => { const n = num(v); return n == null ? '—' : Math.round(n).toLocaleString('en-US'); };
export const fmtMoney = (v) => { const n = num(v); return n == null ? '—' : '$' + Math.round(n).toLocaleString('en-US'); };
export const fmtBase = (v) => { const n = num(v); return n == null ? '—' : '$' + n.toFixed(2); };
/** 'just now' · 'n min ago' · 'n h ago' · 'n d ago' · 'Sync unknown' when null. */
export function syncedLabel(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

/* ---------------------------------------------------------- the path */
const day = (iso) => { if (!iso) return null; const d = new Date(iso); return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };   // the LockedStateV2 shape: Sep 3, 2026
/** The drawer's 4-step path — IN at the meeting → Pre-order → Ordered →
 *  Locked in Developments — from the card alone. state: done · cur · todo. */
export function pathSteps(card) {
  const c = card || {};
  const st = statusOf(c);
  const inBook = !!c.book_line_id;
  const ordered = c.status === 'order' || !!c.cl_number;
  const manual = st?.key === 'man';
  return [
    c.decision === 'in' ? { key: 'in', title: 'IN at the meeting', sub: `Round ${c.decided_at ? day(c.decided_at) : '2027'}`, state: 'done' }
      : c.decision === 'out' ? { key: 'in', title: 'Decided OUT', sub: c.decided_at ? day(c.decided_at) : 'the meeting', state: 'done', tone: 'red' }
        : { key: 'in', title: 'Not discussed', sub: 'no decision row', state: 'todo' },
    { key: 'pre', title: 'Pre-order', sub: inBook ? `in the order data${c.order_date ? ' · ' + day(c.order_date) : ''}` : c.procurement === 'preorder' ? 'procurement set · no Book row yet' : 'waiting for the order file',
      state: ordered ? 'done' : (inBook || c.procurement === 'preorder') ? 'cur' : 'todo' },
    { key: 'ord', title: 'Ordered', sub: ordered ? (c.cl_number ? `${c.cl_number} in the order data` : 'procurement set to order') : manual ? `manual${c.promotion?.at ? ' · ' + day(c.promotion.at) : ''}${c.promotion?.expected_cl ? ' · expected ' + c.promotion.expected_cl : ''}` : 'needs a CL number',
      state: ordered ? 'cur' : manual ? 'cur' : 'todo', tone: ordered ? 'ord' : manual ? 'man' : undefined },
    { key: 'lock', title: 'Locked in Developments', sub: c.locked_since ? `moved to order · left at ${c.left_stage === '_done' ? 'Done' : c.left_stage || 'no stage'}` : 'not yet', state: c.locked_since ? 'done' : 'todo' },
  ];
}
